import { DocumentDuplicateIcon } from '@heroicons/react/24/solid';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { cn } from '@/utils/classname';

export interface CopyIconButtonProps {
  /** Value to copy to clipboard */
  value: string;
  /** Label for toast message (e.g., "Email", "Webhook URL") */
  label: string;
  /** Optional additional class names */
  className?: string;
  /** Optional data-testid */
  'data-testid'?: string;
}

/**
 * Inline copy icon button with green flash feedback.
 *
 * Provides visual confirmation via green flash (1500ms) and toast notification
 * when value is successfully copied to clipboard.
 *
 * Pattern matches existing implementations in:
 * - OutgoingWebhookSection (Copy URL button)
 * - ScanSettingsCards (scan header copy)
 * - WebhookURLCard (webhook URL copy)
 *
 * Usage:
 * ```tsx
 * <CopyIconButton value="user@example.com" label="Email" />
 * ```
 */
export const CopyIconButton: React.FC<CopyIconButtonProps> = ({
  value,
  label,
  className,
  'data-testid': testId,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row selection or other parent interactions

    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied to clipboard`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (error) {
      console.error('Clipboard write failed:', error);
      toast.error('Failed to copy to clipboard');
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={cn(
        'shrink-0 rounded p-1 transition-colors',
        'focus:ring-chariot-brand-primary focus:ring-2 focus:outline-none',
        copied
          ? 'bg-green-500/10 text-green-600 ring-1 ring-green-500/20'
          : 'text-chariot-text-tertiary hover:text-chariot-text-primary hover:bg-chariot-background-secondary',
        className
      )}
      aria-label={`Copy ${label} to clipboard`}
      data-testid={testId || `copy-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <DocumentDuplicateIcon className="size-4" />
    </button>
  );
};
